/**
 * Notifications Service - API Client
 * Handles all notification-related API calls
 */
import api from '@/utils/api';
import type {
  Notification,
  NotificationStats,
  NotificationFilters,
  NotificationPreference,
  PaginatedNotifications,
} from '../types/notification.types';

// ============================================================================
// Types
// ============================================================================

interface ApiResponse<T> {
  success?: boolean;
  data?: T;
  message?: string;
}

interface UnreadCountResponse {
  count: number;
}

interface BatchResponse {
  count: number;
}

// ============================================================================
// Service
// ============================================================================

export const notificationsService = {
  // ==================== NOTIFICATIONS ====================

  /**
   * Get notifications with pagination and filters
   */
  async getAll(filters?: NotificationFilters): Promise<PaginatedNotifications> {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.category) params.append('category', filters.category);
      if (filters.type) params.append('type', filters.type);
      if (filters.isRead !== undefined) params.append('isRead', String(filters.isRead));
      if (filters.isDismissed !== undefined) params.append('isDismissed', String(filters.isDismissed));
      if (filters.isArchived !== undefined) params.append('isArchived', String(filters.isArchived));
      if (filters.sourceModule) params.append('sourceModule', filters.sourceModule);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.search) params.append('search', filters.search);
      if (filters.page) params.append('page', String(filters.page));
      if (filters.limit) params.append('limit', String(filters.limit));
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    }

    const queryString = params.toString();
    const url = queryString ? `/notifications?${queryString}` : '/notifications';

    return api.get(url) as Promise<PaginatedNotifications>;
  },

  /**
   * Get notification by ID
   */
  async getById(id: string): Promise<Notification> {
    return api.get(`/notifications/${id}`) as Promise<Notification>;
  },

  /**
   * Get unread count
   */
  async getUnreadCount(): Promise<number> {
    const response = await api.get('/notifications/unread-count') as UnreadCountResponse;
    return response.count;
  },

  /**
   * Get notification stats
   */
  async getStats(): Promise<NotificationStats> {
    return api.get('/notifications/stats') as Promise<NotificationStats>;
  },

  // ==================== ACTIONS ====================

  /**
   * Mark a notification as read
   */
  async markAsRead(id: string): Promise<Notification> {
    return api.patch(`/notifications/${id}/read`) as Promise<Notification>;
  },

  /**
   * Mark multiple notifications as read
   */
  async markManyAsRead(ids: string[]): Promise<number> {
    const response = await api.patch('/notifications/read', { ids }) as BatchResponse;
    return response.count;
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<number> {
    const response = await api.patch('/notifications/read-all') as BatchResponse;
    return response.count;
  },

  /**
   * Dismiss a notification
   */
  async dismiss(id: string): Promise<Notification> {
    return api.patch(`/notifications/${id}/dismiss`) as Promise<Notification>;
  },

  /**
   * Dismiss multiple notifications
   */
  async dismissMany(ids: string[]): Promise<number> {
    const response = await api.patch('/notifications/dismiss', { ids }) as BatchResponse;
    return response.count;
  },

  /**
   * Archive a notification
   */
  async archive(id: string): Promise<Notification> {
    return api.patch(`/notifications/${id}/archive`) as Promise<Notification>;
  },

  /**
   * Delete a notification
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/notifications/${id}`);
  },

  // ==================== PREFERENCES ====================

  /**
   * Get user notification preferences
   */
  async getPreferences(): Promise<NotificationPreference[]> {
    return api.get('/notifications/preferences') as Promise<NotificationPreference[]>;
  },

  /**
   * Update a notification preference
   */
  async updatePreference(
    type: string,
    data: Partial<Omit<NotificationPreference, 'id' | 'type'>>
  ): Promise<NotificationPreference> {
    return api.put(`/notifications/preferences/${type}`, data) as Promise<NotificationPreference>;
  },

  /**
   * Reset all preferences to default
   */
  async resetPreferences(): Promise<void> {
    await api.delete('/notifications/preferences');
  },

  // ==================== DEVICES (Push Notifications) ====================

  /**
   * Get registered devices
   */
  async getDevices(): Promise<unknown[]> {
    return api.get('/notifications/devices') as Promise<unknown[]>;
  },

  /**
   * Register a device for push notifications
   */
  async registerDevice(data: {
    deviceId: string;
    deviceType: 'BROWSER' | 'IOS' | 'ANDROID' | 'DESKTOP';
    deviceName?: string;
    fcmToken?: string;
    webPushEndpoint?: string;
    webPushKeys?: { p256dh: string; auth: string };
  }): Promise<unknown> {
    return api.post('/notifications/devices', data);
  },

  /**
   * Unregister a device
   */
  async unregisterDevice(deviceId: string): Promise<void> {
    await api.delete(`/notifications/devices/${deviceId}`);
  },

  // ==================== WEB PUSH (VAPID-based) ====================

  /**
   * Get VAPID public key for browser push subscription
   */
  async getVapidKey(): Promise<{ publicKey: string; enabled: boolean }> {
    const response = await api.get('/notifications/push/vapid-key') as ApiResponse<{ publicKey: string; enabled: boolean }>;
    return response.data!;
  },

  /**
   * Subscribe to browser push notifications
   */
  async subscribePush(subscription: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    deviceName?: string;
  }): Promise<{ deviceId: string; isActive: boolean }> {
    const response = await api.post('/notifications/push/subscribe', subscription) as ApiResponse<{ deviceId: string; isActive: boolean }>;
    return response.data!;
  },

  /**
   * Unsubscribe from browser push notifications
   */
  async unsubscribePush(data: { endpoint?: string; deviceId?: string }): Promise<void> {
    // Build URL with query params since api.delete only accepts url
    const params = new URLSearchParams();
    if (data.endpoint) params.append('endpoint', data.endpoint);
    if (data.deviceId) params.append('deviceId', data.deviceId);
    const queryString = params.toString();
    const url = queryString ? `/notifications/push/unsubscribe?${queryString}` : '/notifications/push/unsubscribe';
    await api.delete(url);
  },

  /**
   * Get push subscriptions
   */
  async getPushSubscriptions(): Promise<Array<{
    deviceId: string;
    deviceType: string;
    deviceName: string | null;
    isActive: boolean;
    lastUsedAt: string;
    createdAt: string;
  }>> {
    const response = await api.get('/notifications/push/subscriptions') as ApiResponse<unknown[]>;
    return response.data as Array<{
      deviceId: string;
      deviceType: string;
      deviceName: string | null;
      isActive: boolean;
      lastUsedAt: string;
      createdAt: string;
    }>;
  },

  /**
   * Send a test push notification
   */
  async sendTestPush(): Promise<{
    notificationId: string;
    pushResults: { total: number; success: number; failed: number };
  }> {
    const response = await api.post('/notifications/push/test') as ApiResponse<{
      notificationId: string;
      pushResults: { total: number; success: number; failed: number };
    }>;
    return response.data!;
  },
};

export default notificationsService;
